import {
  ChangeDetectorRef,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  Inject,
  Input,
  OnChanges,
  PLATFORM_ID,
  SimpleChanges,
} from '@angular/core';
import { CommonModule, DOCUMENT, isPlatformBrowser } from '@angular/common';
import { ImageComponent } from '../../atoms/image/image.component';
import { LinkComponent } from '../../atoms/link/link.component';
import { ButtonComponent } from '../../molecules/button/button.component';
import { IconComponent } from '../../atoms/icon/icon.component';
import { Colors, Sizes } from '../../../shared/enums/shared.enums';
import { ButtonType } from '../../../shared/enums/button-type.enums';
import { HrefType } from '../../../shared/enums/href-type.enums';
import {
  CaasLink,
  CaasStage,
  CaasTeaser2Link,
  CaasTeaser3Link,
} from '../../../shared/interfaces/caas/caas.interface';
import { Router } from '@angular/router';
import { ImageMetaData } from '../../../shared/interfaces/meta-data.interface';
import { ImagesService } from '../../../services/images.service';
import { CapitalizePipe } from '../../capitalize.pipe';
import { CaasBodytextService } from '../../../services/caas-bodytext.service';
import { safeAssign } from '../../../shared/utils/safe-assign.util';
import { ErrorHandlerService } from '../../../services/error-handler.service';
import { HomeStageLoaderComponent } from './home-stage-loader/home-stage-loader.component';
@Component({
  selector: 'hdi-home-stage',
  standalone: true,
  templateUrl: './home-stage.component.html',
  styleUrl: './home-stage.component.scss',
  imports: [
    CommonModule,
    ImageComponent,
    LinkComponent,
    ButtonComponent,
    IconComponent,
    CapitalizePipe,
    HomeStageLoaderComponent,
  ],
  host: {
    id: crypto.randomUUID().toString(),
  },
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class HomeStageComponent implements OnChanges {
  Sizes = Sizes;
  BgColors = Colors;
  ButtonType = ButtonType;

  @Input() buttonType: ButtonType = ButtonType.SECONDARY;
  @Input() iconButton = 'arrow-right';
  @Input() teaser1_header = '';
  @Input() teaser1_image = '';
  @Input() teaser1_hrefType: HrefType = HrefType.ANGULAR_ROUTE;
  @Input() teaser1_href = '';
  @Input() teaser1_fragment = '';
  @Input() teaser1_title = '';
  @Input() teaser1_label = '';
  @Input() teaser1_imageTitle = '';
  @Input() teaser1_imageAlt = '';
  @Input() teaser2_header = '';
  @Input() teaser2_icon = 'user-lock';
  @Input() teaser2_hrefType: HrefType = HrefType.ANGULAR_ROUTE;
  @Input() teaser2_href = '';
  @Input() teaser2_fragment = '';
  @Input() teaser2_label = '';
  @Input() teaser2_title = '';
  @Input() teaser3_header = '';
  @Input() teaser3_image = '';
  @Input() teaser3_hrefType: HrefType = HrefType.ANGULAR_ROUTE;
  @Input() teaser3_href = '';
  @Input() teaser3_fragment = '';
  @Input() teaser3_label = '';
  @Input() teaser3_title = '';
  @Input() teaser3_imageTitle = '';
  @Input() teaser3_imageAlt = '';
  @Input() caasData?: CaasStage;
  @Input() imagesMetaData?: ImageMetaData[];

  public isLoading = true;

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly caasBodytextService: CaasBodytextService,
    private readonly router: Router,
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly imagesService: ImagesService,
    private readonly errorHandler: ErrorHandlerService,
    @Inject(PLATFORM_ID) private readonly platformId: object
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['imagesMetaData']?.currentValue) {
      this.imagesService.setImagesMetaData(
        changes['imagesMetaData'].currentValue
      );
    }
    if (
      changes['caasData']?.currentValue &&
      this.caasData &&
      Object.keys(this.caasData || {}).length
    ) {
      this.setCaasContent();
      if (isPlatformBrowser(this.platformId)) {
        this.isLoading = false;
      }

      this.cdr.detectChanges();
    }
  }

  setCaasContent(): void {
    safeAssign(
      () => {
        this.teaser1_header =
          this.caasData?.formData?.st_teaser1_header?.value ||
          this.teaser1_header;
      },
      'Set teaser1_header',
      this.errorHandler
    );
    safeAssign(
      () => {
        this.teaser1_image =
          this.caasData?.formData?.st_teaser1_image?.value?.url ||
          this.teaser1_image;
      },
      'Set teaser1_image',
      this.errorHandler
    );
    safeAssign(
      () => {
        this.teaser1_hrefType = this.determineHrefType(
          this.caasData?.formData?.st_teaser1_link
        );
      },
      'Set teaser1_hrefType',
      this.errorHandler
    );
    safeAssign(
      () => {
        this.teaser1_href =
          this.determineHref(this.caasData?.formData?.st_teaser1_link) ||
          '/404';
      },
      'Set teaser1_href',
      this.errorHandler
    );
    safeAssign(
      () => {
        this.teaser1_fragment =
          this.caasData?.formData?.st_teaser1_link?.value?.formData
            ?.st_anchor_name?.value || '';
      },
      'Set teaser1_fragment',
      this.errorHandler
    );
    safeAssign(
      () => {
        this.teaser1_label =
          this.caasData?.formData?.st_teaser1_link?.value?.formData
            ?.lt_link_label?.value || '';
      },
      'Set teaser1_label',
      this.errorHandler
    );
    safeAssign(
      () => {
        this.teaser1_title =
          this.caasData?.formData?.st_teaser1_link?.value?.formData
            ?.lt_link_title?.value || '';
      },
      'Set teaser1_title',
      this.errorHandler
    );
    safeAssign(
      () => {
        this.teaser1_imageAlt =
          this.imagesService.getAltTextById(
            this.caasData?.formData?.st_teaser1_image?.value?.identifier ?? ''
          ) ?? '';
      },
      'Set teaser1_imageAlt',
      this.errorHandler
    );
    safeAssign(
      () => {
        this.teaser1_imageTitle =
          this.caasData?.formData?.st_teaser1_image_title?.value ?? '';
      },
      'Set teaser1_imageTitle',
      this.errorHandler
    );
    safeAssign(
      () => {
        this.teaser2_header =
          this.caasData?.formData?.st_teaser2_header?.value ||
          this.teaser2_header;
      },
      'Set teaser2_header',
      this.errorHandler
    );
    safeAssign(
      () => {
        this.teaser2_icon =
          this.caasData?.formData?.st_teaser2_icon?.value || 'user-lock';
      },
      'Set teaser2_icon',
      this.errorHandler
    );
    safeAssign(
      () => {
        this.teaser2_hrefType = this.determineHrefType(
          this.caasData?.formData?.st_teaser2_link
        );
      },
      'Set teaser2_hrefType',
      this.errorHandler
    );
    safeAssign(
      () => {
        this.teaser2_href =
          this.determineHref(this.caasData?.formData?.st_teaser2_link) ||
          '/services';
      },
      'Set teaser2_href',
      this.errorHandler
    );
    safeAssign(
      () => {
        this.teaser2_label =
          this.caasData?.formData?.st_teaser2_link?.value?.formData
            ?.lt_link_label?.value || this.teaser2_label;
      },
      'Set teaser2_label',
      this.errorHandler
    );
    safeAssign(
      () => {
        this.teaser2_title =
          this.caasData?.formData?.st_teaser2_link?.value?.formData
            ?.lt_link_title?.value || '';
      },
      'Set teaser2_title',
      this.errorHandler
    );
    safeAssign(
      () => {
        this.teaser2_fragment =
          this.caasData?.formData?.st_teaser2_link?.value?.formData
            ?.st_anchor_name?.value || '';
      },
      'Set teaser2_fragment',
      this.errorHandler
    );
    safeAssign(
      () => {
        this.teaser3_header =
          this.caasData?.formData?.st_teaser3_header?.value ||
          this.teaser3_header;
      },
      'Set teaser3_header',
      this.errorHandler
    );
    safeAssign(
      () => {
        this.teaser3_image =
          this.caasData?.formData?.st_teaser3_image?.value?.url ||
          this.teaser3_image;
      },
      'Set teaser3_image',
      this.errorHandler
    );
    safeAssign(
      () => {
        this.teaser3_hrefType = this.determineHrefType(
          this.caasData?.formData?.st_teaser3_link
        );
      },
      'Set teaser3_hrefType',
      this.errorHandler
    );
    safeAssign(
      () => {
        this.teaser3_href =
          this.determineHref(this.caasData?.formData?.st_teaser3_link) || '';
      },
      'Set teaser3_href',
      this.errorHandler
    );
    safeAssign(
      () => {
        this.teaser3_label =
          this.caasData?.formData?.st_teaser3_link?.value?.formData
            ?.lt_link_label?.value || this.teaser3_label;
      },
      'Set teaser3_label',
      this.errorHandler
    );
    safeAssign(
      () => {
        this.teaser3_title =
          this.caasData?.formData?.st_teaser3_link?.value?.formData
            ?.lt_link_title?.value || '';
      },
      'Set teaser3_title',
      this.errorHandler
    );
    safeAssign(
      () => {
        this.teaser3_fragment =
          this.caasData?.formData?.st_teaser3_link?.value?.formData
            ?.st_anchor_name?.value || '';
      },
      'Set teaser3_fragment',
      this.errorHandler
    );
  }

  /**
   * Determines the href type based on the provided form data.
   * @param link - The link form data.
   * @returns The HrefType.
   */
  private determineHrefType(
    link: CaasLink | CaasTeaser2Link | CaasTeaser3Link | undefined
  ): HrefType {
    let formData:
      | CaasLink['value']['formData']
      | CaasTeaser2Link['value']['formData']
      | CaasTeaser3Link['value']['formData'] = undefined;
    let linkType = '';

    if (link?.value?.formData) {
      safeAssign(
        () => {
          formData = link.value.formData;
          if (formData?.lt_link_external?.value) {
            return HrefType.EXTERNAL;
          } else {
            return HrefType.ANGULAR_ROUTE;
          }
        },
        'Check external link',
        this.errorHandler
      );

      // Otherwise, fallback to the link type identifier
      safeAssign(
        () => {
          linkType = formData?.lt_link_type?.value?.identifier || '';
        },
        'Set link type',
        this.errorHandler
      );
    }

    return linkType === 'external' ? HrefType.EXTERNAL : HrefType.ANGULAR_ROUTE;
  }
  /**
   * Determines the href value based on internal and external link fields.
   * @param link - The link form data.
   * @returns The href value.
   */
  private determineHref(
    link: CaasLink | CaasTeaser2Link | CaasTeaser3Link | undefined
  ): string | null {
    let externalLink = undefined;
    let internalLink = undefined;
    safeAssign(
      () => {
        externalLink = link?.value?.formData?.lt_link_external?.value;
      },
      'Check external link',
      this.errorHandler
    );
    if (externalLink) return externalLink;

    safeAssign(
      () => {
        internalLink = link?.value?.formData?.lt_link_internal?.value;
      },
      'Check internal link',
      this.errorHandler
    );
    return internalLink ? this.getInternalUrl(link as CaasLink) : null;
  }

  private getInternalUrl(internalLink: CaasLink): string {
    return this.caasBodytextService.foundHref(internalLink);
  }
}
